import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import type { AuthState } from '../types';
import SEOHead from '../components/SEOHead';

interface KnowMoreProps {
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const KnowMore: React.FC<KnowMoreProps> = ({ authState, setAuthState }) => {
  return (
    <Layout authState={authState} setAuthState={setAuthState}>
      <SEOHead
        title="How it Works"
        description="Learn how to get started with MarketEye, from signing in to setting up alerts and tracking your portfolio."
        canonicalUrl="/know-more"
      />
      <div className="max-w-6xl mx-auto">

        {/* Getting Started Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-8">Get Started in 4 Simple Steps</h2>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-600">1</span>
                </div>
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sign In with Google</h3>
                <p className="text-gray-700 mb-4">
                  Sign in securely using your Google account. Your account is secured with industry-standard encryption.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-green-600">2</span>
                </div>
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Add Your Holdings</h3>
                <p className="text-gray-700 mb-4">
                  Import your portfolio holdings using one of our supported methods:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">CSV Upload</h4>
                    <p className="text-sm text-gray-600">
                      Get your holdings from your broker and drag and drop the CSV file. That's it !
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Manual Entry</h4>
                    <p className="text-sm text-gray-600">
                      Add stocks quickly with quantity and average price
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Broker API (Coming Soon)</h4>
                    <p className="text-sm text-gray-600">
                      Connect directly to supported brokers for automatic sync (Coming Soon)
                    </p>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>Recommended:</strong> Start with CSV upload for quick setup
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-yellow-600">3</span>
                </div>
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Set Up Alerts & Rules</h3>
                <p className="text-gray-700 mb-4">
                  Quickly set up intelligent alerts and automated rules to protect your investments
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Price Alerts</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Price crossing thresholds</li>
                      <li>• Percentage change alerts</li>
                      <li>• Fixed / automatically increasing thresholds</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Alerting</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Email</li>
                      <li>• WhatsApp</li>
                      <li>• Automatic sell orders (Coming Soon)</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Tip:</strong> Click on "Price Guard" after adding your holdings to get started in just a few clicks
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-purple-600">4</span>
                </div>
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sit Back & Relax</h3>
                <p className="text-gray-700 mb-4">
                  We've got it from here. We'll send you alerts when you need to take action.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Live Price Tracking</h4>
                    <p className="text-sm text-gray-600">
                      We track the price of your holdings live, every minute.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Dynamic Thresholds</h4>
                    <p className="text-sm text-gray-600">
                      Thresholds (if configured) increase along with price, so you can reap the most of any price increase
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Rule Triggers</h4>
                    <p className="text-sm text-gray-600">
                      We'll trigger alerts when your holdings cross your defined thresholds.
                    </p>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-800">
                    <strong>Pro Tip:</strong> Review your alert history monthly to tweak any rules.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Key Features */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Key Features</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">Real-time Alerts</h3>
                  <p className="text-sm text-gray-600">Get instant notifications via email and WhatsApp</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">Automated Rules</h3>
                  <p className="text-sm text-gray-600">Set up intelligent trading rules and automation</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">Portfolio Analytics</h3>
                  <p className="text-sm text-gray-600">Comprehensive portfolio performance tracking</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">Multi-Broker Support (Coming Soon)</h3>
                  <p className="text-sm text-gray-600">Connect multiple broker accounts in one place (Coming Soon)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Tiers */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Subscription Tiers</h2>
            {authState.isAuthenticated ? (
              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Tier</h3>
                  <ul className="list-disc pl-6 text-gray-700 mb-4">
                    <li>Add alerts for up to 50 holdings</li>
                    <li>Alerts will be sent for the first 5 triggers in a month</li>
                    <li>Email notifications only</li>
                  </ul>
                </div>
                <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Tier</h3>
                  <ul className="list-disc pl-6 text-gray-700 mb-4">
                    <li>Add alerts for up to 200 holdings</li>
                    <li>Alerts will be sent for all triggers in a month</li>
                    <li>Multiple notification channels (Email, WhatsApp)</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Pricing information available after login</p>
                <Link
                  to="/login?redirect=/know-more"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Sign In to View Pricing
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* CTA Section - Only show for non-authenticated users */}
        {!authState.isAuthenticated && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Join savvy investors who are already using MarketEye to make smarter
              investment decisions. It only takes a few minutes to set up your account.
            </p>
            <div className="space-x-4">
              <Link
                to="/login"
                className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Sign Up Now
              </Link>
              <Link
                to="/contact"
                className="inline-block border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KnowMore; 